/**
 * Wire types of the order assistant stream contract, version 1
 * (`order-assistant/docs/stream-contract.md`, KAN-383). Copied from the
 * contract, not designed here: when the contract changes, change this file to
 * match and bump {@link SUPPORTED_CONTRACT_VERSION}.
 *
 * All JSON keys are snake_case and amounts are decimal major units in
 * `currency`, as in the core API.
 */

/** The contract version this client was written against (`session` event). */
export const SUPPORTED_CONTRACT_VERSION = 1;

/** Section 6: the catalog an answer drew on. */
export type CatalogSearched = "master" | "supplier" | "order_history" | "none";

/** Section 7.2. */
export type ErrorCode =
  | "limit_reached"
  | "session_ended"
  | "session_customer_mismatch"
  | "token_expired"
  | "busy"
  | "disabled"
  | "invalid_request"
  | "internal";

/** Section 7.1 / 4.9: `{ code, message, ...fields }`. `message` is an English
 *  fallback that the drawer never shows; it picks its own copy by `code`. */
export type ErrorPayload = { code: ErrorCode; message: string } & Record<
  string,
  unknown
>;

export type ErrorBody = { error: ErrorPayload };

/** Section 2.1. */
export type StatusResponse = { enabled: boolean };

/** Section 2.2. */
export type ChatRequest = {
  /** 1 to 4000 characters after trimming. */
  message: string;
  page?: {
    page_type?: string;
    product_id?: string | null;
    query?: string | null;
  } | null;
};

export const MAX_MESSAGE_LENGTH = 4000;

// --- Events (section 4) ------------------------------------------------------

export type SessionEvent = {
  session_id: string;
  contract_version: number;
  resumed: boolean;
};

export type TextDeltaEvent = { text: string };

export type ProgressEvent = { message: string; tool?: string; step?: number };

export type ToolCallEvent = { tool: string; id: string; label?: string };

export type ToolResultEvent = {
  tool: string;
  id: string;
  status: "ok" | "error" | "blocked";
  is_error: boolean;
};

/** Section 5. */
export type MasterProductCard = {
  kind: "master";
  master_product_id: string;
  variant_id: string | null;
  offer_id: string;
  seller_id: string;
  seller_name: string;
  title: string;
  brand: string | null;
  thumbnail: string | null;
  price: number;
  currency: string;
  in_stock: boolean;
  product_type: string | null;
  reason: string | null;
};

export type SupplierProductCard = {
  kind: "supplier";
  supplier_product_id: string;
  supplier_variant_id: string;
  title: string;
  variant_title: string | null;
  brand: string | null;
  seller_id: string;
  seller_name: string;
  thumbnail: string | null;
  public_price: number | null;
  currency: string;
  /** Present only for a salon with a claimed trade account; never null. */
  trade_price?: number;
  available: boolean;
  master_product_id: string | null;
  reason: string | null;
};

export type ProductCard = MasterProductCard | SupplierProductCard;

/** How wide a search was: `own_suppliers` only the salon's own claimed or
 *  matched suppliers, `all` not limited that way. Optional everywhere it
 *  appears, so a reply from before it existed reads as it always did. */
export type SearchScope = "own_suppliers" | "all";

export type CardsEvent = {
  cards_id: string;
  catalog_searched: "master" | "supplier";
  /** The scope of the search that found these cards, when the service knows it. */
  search_scope?: SearchScope;
  suppliers?: { seller_id: string; name: string }[];
  title: string | null;
  layout: "carousel" | "grid" | "list";
  cards: ProductCard[];
};

export type UiEvent = {
  component: string;
  payload: unknown;
  stream_id: string;
};

export type SuggestionsPayload = { suggestions: string[] };

export type DoneEvent = {
  stop_reason: "end_turn" | "max_tokens" | "tool_use" | null;
  catalogs_searched: CatalogSearched[];
  /** The scope of the turn's searches of each catalog, when the service knows it. */
  search_scope?: Partial<Record<"master" | "supplier", SearchScope>>;
};

export type ErrorEvent = ErrorPayload;

/** An event the drawer understands, discriminated by `type`. */
export type StreamEvent =
  | { type: "session"; data: SessionEvent }
  | { type: "text_delta"; data: TextDeltaEvent }
  | { type: "progress"; data: ProgressEvent }
  | { type: "tool_call"; data: ToolCallEvent }
  | { type: "tool_result"; data: ToolResultEvent }
  | { type: "cards"; data: CardsEvent }
  | { type: "ui_partial"; data: UiEvent }
  | { type: "ui"; data: UiEvent }
  | { type: "done"; data: DoneEvent }
  | { type: "error"; data: ErrorEvent };

/** A frame as read off the wire, before the type is checked. */
export type RawFrame = { type: string; data: unknown };

export const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set([
  "session",
  "text_delta",
  "progress",
  "tool_call",
  "tool_result",
  "cards",
  "ui_partial",
  "ui",
  "done",
  "error"
]);
